import { useState } from "react";
import {
  Box,
  CloseIcon,
  Column,
  IconButton,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";
import { Card } from "src/components/card";
import { AttachmentChip } from "src/components/llm/components/attachment-chip";
import { AttachmentTypeTile } from "src/components/llm/components/attachment-type-tile";
import { attachmentCategory } from "src/components/llm/utils/attachment-presentation";
import {
  base64ToBlobUrl,
  fileTypeDescription,
  isImage,
} from "src/components/llm/utils/file-processing";
import { ImageThumbnail, IMAGE_THUMBNAIL_SIZES } from "./image-thumbnail";
import { resolveAgentFileUrl } from "src/utils/resolve-agent-file-url";

export type PreviewFile = {
  fileName: string;
  mimeType: string;
  // Base64 when the user is uploading the file, URL when the file is stored in the database.
  base64Data?: string;
  url?: string;
  /**
   * Object URL for the picked `File`. Attachments uploaded straight to storage
   * carry no bytes, so this is the only thing a composing preview can render.
   */
  previewUrl?: string;
};

/**
 * Resolves the displayable src for a file: a presigned URL for persisted files,
 * or an object URL (or base64 data URL) for files still being composed.
 * Exported so callers that build a shared image gallery can compute the same
 * src the card renders.
 */
export const getFileSrc = (file: PreviewFile): string | undefined => {
  if (file.url) {
    return resolveAgentFileUrl(file.url);
  }

  if (file.previewUrl) {
    return file.previewUrl;
  }

  if (file.base64Data) {
    return `data:${file.mimeType};base64,${file.base64Data}`;
  }

  return undefined;
};

/**
 * Markup we must never navigate to. A blob: URL inherits the creating
 * document's origin, so opening an attached HTML file in a tab would execute
 * whatever is in it against the app's origin, with the user's session. The
 * other accepted text formats render as inert plain text, and a PDF opens in
 * the browser's sandboxed viewer.
 */
const NAVIGATION_UNSAFE_MIME_TYPES = new Set([
  "text/html",
  "application/xhtml+xml",
  "image/svg+xml",
]);

const downloadFile = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const openFilePreview = (file: PreviewFile) => {
  // Hand markup to the browser as a download rather than navigating to it.
  const open = NAVIGATION_UNSAFE_MIME_TYPES.has(file.mimeType)
    ? (href: string) => downloadFile(href, file.fileName)
    : (href: string) => window.open(href, "_blank", "noopener");

  // Use presigned URL if available
  if (file.url) {
    open(resolveAgentFileUrl(file.url));
    return;
  }

  // An uploaded attachment's only local handle on its bytes. The store owns
  // this URL, so open it without revoking.
  if (file.previewUrl) {
    open(file.previewUrl);
    return;
  }

  // For base64 data
  if (file.base64Data) {
    // For images, data URLs work fine
    if (isImage(file.mimeType)) {
      open(`data:${file.mimeType};base64,${file.base64Data}`);
      return;
    }

    // For non-images (PDFs, etc.), convert to blob URL for better browser support
    const blobUrl = base64ToBlobUrl(file.base64Data, file.mimeType);
    open(blobUrl);
    // Clean up the blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  }
};

export const FilePreviewCard = ({
  file,
  onRemove,
  imageThumbnailSize = "md",
  showImageNameInline,
  onExpand,
  variant = "card",
}: {
  file: PreviewFile;
  onRemove?: () => void;
  imageThumbnailSize?: "xs" | "sm" | "md" | "lg";
  showImageNameInline?: boolean;
  /** Delegates image expansion to a shared gallery overlay (see ImageThumbnail). */
  onExpand?: () => void;
  /**
   * How a non-image file is drawn. Chat transcripts use the compact chip;
   * the larger card is what every preview panel outside chat still renders.
   */
  variant?: "card" | "chip";
}) => {
  const fileData = getFileSrc(file);
  // The src that failed rather than a boolean, so a file whose source changes
  // gets a fresh attempt without an effect to reset the flag.
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);

  if (isImage(file.mimeType) && fileData && failedImageSrc !== fileData) {
    return (
      <ImageThumbnail
        fileName={file.fileName}
        imageSrc={fileData}
        onRemove={onRemove}
        size={imageThumbnailSize}
        showImageNameInline={showImageNameInline}
        onExpand={onExpand}
        onError={() => setFailedImageSrc(fileData)}
      />
    );
  }

  // An image whose source hasn't arrived yet holds the thumbnail's footprint
  // rather than collapsing into a chip. A just-sent attachment has no URL for
  // the moment it takes the server to persist it, and swapping shapes for that
  // moment reads as the image being replaced by a file and then coming back.
  if (variant === "chip" && isImage(file.mimeType)) {
    return (
      <MissingImageThumbnail
        fileName={file.fileName}
        size={imageThumbnailSize}
      />
    );
  }

  if (variant === "chip") {
    return (
      <AttachmentChip
        fileName={file.fileName}
        mimeType={file.mimeType}
        onPreview={fileData ? () => openFilePreview(file) : undefined}
      />
    );
  }

  return <NonImageFileCard file={file} onRemove={onRemove} />;
};

const NonImageFileCard = ({
  file,
  onRemove,
}: {
  file: PreviewFile;
  onRemove?: () => void;
}) => {
  const hasData = file.url || file.previewUrl || file.base64Data;

  return (
    <Card
      px={3}
      py={2}
      height={16}
      flexShrink={0}
      maxWidth="2xs"
      justifyContent="center"
      onClick={hasData ? () => openFilePreview(file) : undefined}
    >
      <Box display="flex" alignItems="center" gap={3}>
        <AttachmentTypeTile
          category={attachmentCategory(file.mimeType, file.fileName)}
          size="xl"
        />
        <Column overflow="hidden" minWidth={0} maxWidth="100%">
          <TextWithTooltip size="md">{file.fileName}</TextWithTooltip>
          <Text color="text.secondary" size="sm">
            {fileTypeDescription(file.mimeType)}
          </Text>
        </Column>
        {onRemove && (
          <Box>
            <IconButton
              icon={CloseIcon}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              size="xs"
              shape="circle"
              aria-label="remove"
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

/**
 * Stands in for an image that has no source to draw. Sized like the thumbnail
 * it replaces so the row or grid around it doesn't move, and inert: there is
 * nothing to preview.
 */
const MissingImageThumbnail = ({
  fileName,
  size,
}: {
  fileName: string;
  size: keyof typeof IMAGE_THUMBNAIL_SIZES;
}) => {
  const imgSize = IMAGE_THUMBNAIL_SIZES[size];

  return (
    <Tooltip message={fileName} placement="bottom">
      <Card
        role="img"
        aria-label={fileName}
        p={0}
        width={imgSize}
        height={imgSize}
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <AttachmentTypeTile category="image" size="lg" />
      </Card>
    </Tooltip>
  );
};
